import { ref } from 'vue';
import { CLOCK_UPDATE_INTERVAL_MS } from '@/common/config';

/**
 * Global clock singleton composable
 *
 * Provides a single reactive `now` timestamp that updates every second.
 * This eliminates the need for individual setInterval timers in each component/composable.
 *
 * Benefits:
 * - Single timer for entire application (instead of N timers for N market cards)
 * - Better performance and battery life
 * - Consistent time across all components
 *
 * @example
 * ```ts
 * const { now } = useClock();
 * const timeRemaining = computed(() => deadline - now.value);
 * ```
 */

// Global singleton state - shared across all composable instances
const now = ref<number>(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;
let subscriberCount = 0;

/**
 * Get the global clock instance
 * Auto-starts the clock on first subscription, stops when no subscribers remain
 */
export function useClock() {
  // Start the clock if this is the first subscriber
  if (subscriberCount === 0) {
    startClock();
  }

  subscriberCount++;

  /**
   * Cleanup function for component unmount
   * Call this in onBeforeUnmount if you want to explicitly unsubscribe
   */
  function unsubscribe() {
    subscriberCount = Math.max(0, subscriberCount - 1);

    // Stop the clock if no more subscribers
    if (subscriberCount === 0) {
      stopClock();
    }
  }

  return {
    /** Current timestamp in milliseconds, updates every second */
    now,
    /** Manually unsubscribe from clock updates (optional, for cleanup) */
    unsubscribe,
  };
}

/**
 * Start the global clock timer
 */
function startClock() {
  if (timer !== null) return; // Already running

  now.value = Date.now();
  timer = setInterval(() => {
    now.value = Date.now();
  }, CLOCK_UPDATE_INTERVAL_MS);
}

/**
 * Stop the global clock timer
 */
function stopClock() {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}
